import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

export const NotificationBell = ({ onNavigate }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* guest */ }
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const escape = event => {if(event.key === 'Escape')setOpen(false);};
    document.addEventListener("mousedown", h);
    document.addEventListener('keydown',escape);
    return () => {document.removeEventListener("mousedown", h);document.removeEventListener('keydown',escape);};
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      try { await api.post("/notifications/read"); } catch { /* ignore */ }
    }
  };

  const goTo = (it) => {
    setOpen(false);
    if (onNavigate) onNavigate();
    if (it.link) nav(it.link);
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        data-testid="top-nav-notification-bell"
        className="relative w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Notifications" aria-expanded={open} aria-controls="dayone-notifications">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="top-nav-notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-slate-950">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            id="dayone-notifications" role="region" aria-label="Dernières notifications" data-testid="top-nav-notification-dropdown"
            className="absolute right-0 mt-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden z-50">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <span className="font-head font-700 text-sm text-white">Notifications</span>
              <span className="text-[11px] text-slate-500">{items.length} récentes</span>
            </div>
            <div className="max-h-[380px] overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-4 py-10 text-center text-slate-500 text-sm">
                  <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" />
                  Aucune notification
                </div>
              ) : (
                items.map((it) => (
                  <button
                    key={it.id}
                    onClick={() => goTo(it)}
                    data-testid={`notif-item-${it.kind}`}
                    className={`w-full text-left px-4 py-3 flex gap-3 border-b border-white/5 hover:bg-white/5 transition-colors ${it.read ? "" : "bg-violet-500/5"}`}>
                    <div className="mt-0.5 shrink-0">
                      {!it.read && <span className="w-2 h-2 rounded-full bg-violet-400 block" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-100 truncate">{it.title}</div>
                      {it.body && <div className="text-xs text-slate-400 line-clamp-2 mt-0.5">{it.body}</div>}
                      <div className="text-[10px] text-slate-500 mt-1">{timeAgo(it.created_at)}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
            {items.length > 0 && (
              <div className="px-4 py-2 border-t border-white/10 text-center">
                <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400"><Check className="w-3 h-3" /> Tout est à jour</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
